/**
 * Shared Fluent UI slider component for stage controls.
 * Used by both Inspector v2 StageControlsService and React demo pages.
 */

import {
  Label,
  makeStyles,
  Slider,
  SpinButton,
  tokens,
} from "@fluentui/react-components";
import { type FC, useCallback, useEffect, useRef, useState } from "react";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  label: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  row: {
    alignItems: "center",
    display: "flex",
    gap: tokens.spacingHorizontalS,
  },
  slider: {
    flex: 1,
    minWidth: "80px",
  },
  spinButton: {
    width: "70px",
  },
});

export interface StageSliderProps {
  /** Label displayed above the slider */
  label: string;
  /** Current value */
  value: number;
  /** Minimum value */
  min: number;
  /** Maximum value */
  max: number;
  /** Step increment */
  step: number;
  /** Called when value changes (on release for slider, immediately for spin button) */
  onChange: (value: number) => void;
  /** Optional value formatter for display */
  formatValue?: (value: number) => string;
}

/**
 * Slider + SpinButton combo for numeric stage config values.
 * Emits onChange on slider release (not during drag) for performance.
 */
export const StageSlider: FC<StageSliderProps> = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  formatValue,
}) => {
  const styles = useStyles();

  // Local state for smooth slider dragging
  const [localValue, setLocalValue] = useState(value);

  // Track dragging state with ref (doesn't trigger re-render)
  const isDraggingRef = useRef(false);

  // Sync local value when prop changes (e.g., from external update)
  useEffect(() => {
    if (!isDraggingRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  const handleSliderChange = useCallback(
    (_: unknown, data: { value: number }) => {
      isDraggingRef.current = true;
      setLocalValue(data.value);
    },
    [],
  );

  const handleSliderCommit = useCallback(() => {
    isDraggingRef.current = false;
    onChange(localValue);
  }, [localValue, onChange]);

  const handleSpinButtonChange = useCallback(
    (_: unknown, data: { value?: number | null; displayValue?: string }) => {
      const newValue = data.value ?? Number.parseFloat(data.displayValue ?? "");
      if (!Number.isNaN(newValue)) {
        const clamped = Math.min(max, Math.max(min, newValue));
        setLocalValue(clamped);
        onChange(clamped);
      }
    },
    [min, max, onChange],
  );

  const displayValue = formatValue
    ? formatValue(localValue)
    : localValue.toString();

  return (
    <div className={styles.container}>
      <Label className={styles.label}>
        {label}: {displayValue}
      </Label>
      <div className={styles.row}>
        <Slider
          className={styles.slider}
          max={max}
          min={min}
          onChange={handleSliderChange}
          onPointerUp={handleSliderCommit}
          onTouchEnd={handleSliderCommit}
          step={step}
          value={localValue}
        />
        <SpinButton
          className={styles.spinButton}
          max={max}
          min={min}
          onChange={handleSpinButtonChange}
          step={step}
          value={localValue}
        />
      </div>
    </div>
  );
};
